import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Auth } from '@aws-amplify/auth';
import { Constant } from '../../constant';
import { Approver } from '../../model/my-event';
import { CommonUtil } from '../../util/common-util';
import { AuthService } from '../../auth/auth.service';
import { ToastUtil } from '../../util/toast-util';

@Component({
  selector: 'app-uc-member-select-dialog',
  templateUrl: './uc-member-select-dialog.component.html',
  styleUrl: './uc-member-select-dialog.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCheckboxModule,
  ],
})
export class UcMemberSelectDialogComponent implements OnInit {
  readonly Constant = Constant;

  selectedPosition: number | null = null;
  lastName = '';
  firstName = '';

  officialPositions: { label: string; value: number }[] = [];
  showSpinner = true;

  searchResults: Approver[] = [];
  isSearching = false;
  hasSearched = false;

  selectedMembers: Approver[] = [];

  private excludedUserIds: string[] = [];
  private readonly apiPath = '/union-consultation/searchuser';

  private lastSearchCondition = {
    lastName: '',
    firstName: '',
    position: null as number | null,
  };

  constructor(
    public dialogRef: MatDialogRef<UcMemberSelectDialogComponent>,
    private auth: AuthService,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.excludedUserIds = this.data?.excludedUserIds ?? [];
    this.selectedMembers = [...(this.data?.selectedMembers ?? [])];
    this.loadMaster();
  }

  onSearch(): void {
    if (!this.lastName && !this.firstName && this.selectedPosition === null) {
      this.searchResults = [];
      return;
    }

    if (
      this.lastSearchCondition.lastName === this.lastName &&
      this.lastSearchCondition.firstName === this.firstName &&
      this.lastSearchCondition.position === this.selectedPosition
    ) {
      return;
    }

    this.lastSearchCondition = {
      lastName: this.lastName,
      firstName: this.firstName,
      position: this.selectedPosition,
    };

    this.isSearching = true;
    this.hasSearched = true;
    this.searchResults = [];
    this.search();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.isComposing) return;
    (event.target as HTMLInputElement).blur();
  }

  isSelected(member: Approver): boolean {
    return this.selectedMembers.some(m => m.id === member.id);
  }

  toggleMember(member: Approver): void {
    const index = this.selectedMembers.findIndex(m => m.id === member.id);
    if (index >= 0) {
      this.selectedMembers.splice(index, 1);
    } else {
      this.selectedMembers.push(member);
    }
  }

  onSelect(): void {
    this.dialogRef.close(this.selectedMembers);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private loadMaster(): void {
    if (!this.commonUtil.isOnline()) {
      this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
      return;
    }

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      this.commonUtil.apiGet('/master', options)
        .then(res => {
          const masters = res.data?.master;
          this.officialPositions = ((masters?.[5] as any[]) || []).map(m => ({
            label: m.name,
            value: Number(m.id),
          }));
          this.showSpinner = false;
        })
        .catch(err => {
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
            this.dialogRef.close(false);
          }
        });
    });
  }

  private search(): void {
    const isValidPosition = this.officialPositions.some(p => p.value === this.selectedPosition);

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      options['body'] = {
        user_LastName: this.lastName,
        user_firstName: this.firstName,
        officialPosition: isValidPosition ? this.selectedPosition : null,
        excludedUserIds: this.excludedUserIds,
      };

      this.commonUtil.apiPost(this.apiPath, options)
        .then(res => {
          this.searchResults = this.convertMembers(res.data);
          this.isSearching = false;
        })
        .catch(err => {
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.isSearching = false;
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    });
  }

  private convertMembers(data: any[]): Approver[] {
    const typeMap: { [key: number]: string } = { 1: '本部', 2: '支部', 3: 'エリア' };

    return data.map(user => {
      const affiliations = (user.organizations || []).map((org: any) =>
        org.level === 0 ? org.path : `${typeMap[org.master_type] || ''} ${org.path}`.trim()
      );
      const concurrentAffiliations = (user.concurrent_organizations || []).map((org: any) =>
        `専門部 ${org.path}`.trim()
      );

      return {
        id: user.user_id,
        name: `${user.last_name} ${user.firstName}`,
        position: user.officialposition_name ?? '',
        affiliations: [...affiliations, ...concurrentAffiliations],
      };
    });
  }
}
